import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { usePaginatedApi, useFormSubmit, useAsyncOperation } from '../hooks/useApi';
import { certificatesAPI, clientsAPI } from '../services/api';
import { formatDate, formatDateTime, formatWeight } from '../utils/helpers';
import { useAuth } from '../contexts/AuthContext';
import Table from '../components/Table';
import Button from '../components/Button';
import Input, { Select } from '../components/Input';
import Modal, { ConfirmModal } from '../components/Modal';
import LoadingSpinner from '../components/LoadingSpinner';
import SearchableSelect from '../components/SearchableSelect';
import { 
  DocumentTextIcon, 
  PlusIcon, 
  EyeIcon, 
  CheckCircleIcon,
  XCircleIcon,
  DocumentArrowDownIcon, 
  TrashIcon, 
  CalendarIcon,
  MagnifyingGlassIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';

const CertificatesPage = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const openCertificateFromNavHandledRef = useRef(null);
  const { isOperator, isClient, isManagerOrAdmin, isPeseur, isSuper } = useAuth();
  const superReadOnly = isSuper();
  const [filters, setFilters] = useState({
    search: '',
    clientId: '',
    startDate: '',
    endDate: '',
  });
  const [isGenerateModalOpen, setIsGenerateModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isRejectModalOpen, setIsRejectModalOpen] = useState(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [selectedCertificate, setSelectedCertificate] = useState(null);
  const [certificateToReject, setCertificateToReject] = useState(null);
  const [rejectionReason, setRejectionReason] = useState('');
  const [viewingCertificate, setViewingCertificate] = useState(null);
  const [certificateBills, setCertificateBills] = useState([]);
  const [loadingBills, setLoadingBills] = useState(false);
  const [showAllCertificates, setShowAllCertificates] = useState(false);

  const withListParams = useCallback((base, includeSettledOverride) => {
    const want = typeof includeSettledOverride === 'boolean' ? includeSettledOverride : showAllCertificates;
    return { ...base, includeSettled: want ? 'true' : 'false' };
  }, [showAllCertificates]);
  
  const {
    data: certificates,
    pagination,
    loading,
    updateParams,
    goToPage,
    refresh,
  } = usePaginatedApi(certificatesAPI.getAll, { includeSettled: 'false' });

  const clientListApi = isPeseur() ? certificatesAPI.getAvailableClients : clientsAPI.getAll;
  const clientListInitialParams = isPeseur() ? {} : { limit: 100 };
  const { data: clients, loading: clientsLoading } = usePaginatedApi(clientListApi, clientListInitialParams);

  // Delete operation
  const { execute: executeDelete, loading: deleteLoading } = useAsyncOperation();
  const { execute: executeReview, loading: reviewLoading } = useAsyncOperation();

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  // Debounced search for automatic type-ahead
  const searchTimeoutRef = useRef(null);
  const updateParamsRef = useRef(updateParams);
  
  // Keep updateParams ref up to date
  useEffect(() => {
    updateParamsRef.current = updateParams;
  }, [updateParams]);
  
  useEffect(() => {
    // Clear existing timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    // Set new timeout for debounced search
    searchTimeoutRef.current = setTimeout(() => {
      const params = {};
      // Always include search parameter to ensure it's updated (even if empty to clear)
      const searchValue = filters.search.trim();
      params.search = searchValue || ''; // Explicitly set to empty string if cleared
      
      // Include other filters only if they have values
      if (filters.clientId) params.clientId = filters.clientId;
      if (filters.startDate) params.startDate = filters.startDate;
      if (filters.endDate) params.endDate = filters.endDate;
      
      updateParamsRef.current(withListParams(params));
    }, 500); // 500ms debounce delay

    // Cleanup function
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [filters.search, filters.clientId, filters.startDate, filters.endDate, withListParams]);

  const handleShowAllCertificatesChange = (e) => {
    const checked = Boolean(e.target.checked);
    setShowAllCertificates(checked);
    const params = {};
    const searchValue = filters.search.trim();
    params.search = searchValue || '';
    if (filters.clientId) params.clientId = filters.clientId;
    if (filters.startDate) params.startDate = filters.startDate;
    if (filters.endDate) params.endDate = filters.endDate;
    updateParams(withListParams(params, checked));
  };

  const handleView = async (certificate) => {
    try {
      const response = await certificatesAPI.getById(certificate.id);
      setViewingCertificate(response.data.certificate);
      setIsViewModalOpen(true);
    } catch (error) {
      console.error('Error fetching certificate details:', error);
    }
  };

  const handleOpenLoadFromCertificate = (loadId) => {
    if (!loadId) return;
    const certificateId = viewingCertificate?.id;
    setIsViewModalOpen(false);
    setViewingCertificate(null);
    navigate('/loads', {
      state: {
        openLoadId: loadId,
        ...(certificateId ? { openCertificateId: certificateId } : {})
      }
    });
  };

  const handleOpenBillFromCertificate = (billId) => {
    if (!billId) return;
    const certificateId = viewingCertificate?.id;
    setIsViewModalOpen(false);
    setViewingCertificate(null);
    navigate('/bills', {
      state: {
        openBillId: billId,
        ...(certificateId ? { openCertificateId: certificateId } : {})
      }
    });
  };

  const handleOpenClientFromCertificate = (clientId) => {
    if (!clientId) return;
    setIsViewModalOpen(false);
    setViewingCertificate(null);
    navigate('/clients', { state: { openClientId: clientId } });
  };

  // Open certificate modal when navigating from another page (e.g. bill details)
  useEffect(() => {
    const openCertificateId = location.state?.openCertificateId;
    const openCertificateNumber = location.state?.openCertificateNumber;
    const navKey = openCertificateId || openCertificateNumber || null;
    if (!navKey) {
      openCertificateFromNavHandledRef.current = null;
      return;
    }
    if (openCertificateFromNavHandledRef.current === navKey) return;
    openCertificateFromNavHandledRef.current = navKey;

    navigate(location.pathname, { replace: true, state: {} });

    (async () => {
      try {
        if (openCertificateId) {
          const response = await certificatesAPI.getById(openCertificateId);
          setViewingCertificate(response.data.certificate);
          setIsViewModalOpen(true);
          return;
        }

        const listResponse = await certificatesAPI.getAll({
          search: openCertificateNumber,
          includeSettled: 'true',
          limit: 20,
          page: 1,
        });
        const certificatesList = Array.isArray(listResponse?.data?.certificates) ? listResponse.data.certificates : [];
        const matchedCertificate = certificatesList.find(
          (c) => String(c.certificateNumber).toLowerCase() === String(openCertificateNumber).toLowerCase()
        ) || certificatesList[0];
        if (!matchedCertificate?.id) return;

        const response = await certificatesAPI.getById(matchedCertificate.id);
        setViewingCertificate(response.data.certificate);
        setIsViewModalOpen(true);
      } catch (err) {
        console.error('Error opening certificate from navigation:', err);
      }
    })();
  }, [location.state?.openCertificateId, location.state?.openCertificateNumber, location.pathname, navigate]);

  const handleDownload = async (certificate) => {
    try {
      // Get current language from i18n
      const currentLanguage = localStorage.getItem('i18nextLng') || 'fr';
      const lang = currentLanguage.startsWith('fr') ? 'fr' : 'en';

      const response = await certificatesAPI.getPDF(certificate.id, { lang });

      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);

      // Open PDF in a new tab only (no download), so it shows once in the browser
      window.open(url, '_blank', 'noopener,noreferrer');

      // Revoke after a delay so the new tab can load the PDF
      setTimeout(() => window.URL.revokeObjectURL(url), 60000);
    } catch (error) {
      console.error('Error downloading certificate PDF:', error);
    }
  };

  const handleDelete = async (certificate) => {
    setSelectedCertificate(certificate);
    setLoadingBills(true);
    setCertificateBills([]);
    
    try {
      // Fetch certificate details which now includes bills
      const response = await certificatesAPI.getById(certificate.id);
      const certData = response.data.certificate;
      
      // Extract bills from certificate data
      if (certData.bills && Array.isArray(certData.bills)) {
        setCertificateBills(certData.bills);
      }
    } catch (error) {
      console.error('Error fetching certificate bills:', error);
      // Continue without bills info
    } finally {
      setLoadingBills(false);
      setIsDeleteModalOpen(true);
    }
  };

  const handleDeleteFromView = (certificate) => {
    if (!certificate?.id) return;
    setIsViewModalOpen(false);
    setViewingCertificate(null);
    handleDelete(certificate);
  };

  const confirmDelete = async () => {
    if (!selectedCertificate) return;
    
    try {
      await executeDelete(
        () => certificatesAPI.delete(selectedCertificate.id),
        {
          showSuccessToast: true,
          successMessage: t('certificates.certificateDeleted'),
        }
      );
      setIsDeleteModalOpen(false);
      setSelectedCertificate(null);
      setCertificateBills([]);
      refresh();
    } catch (error) {
      // Error is already handled by useAsyncOperation with toast
    }
  };

  const handleCertify = async (certificate) => {
    if (!certificate?.id) return;
    try {
      await executeReview(
        () => certificatesAPI.review(certificate.id, { action: 'CERTIFY' }),
        {
          showSuccessToast: true,
          successMessage: t('certificates.certificateCertified'),
        }
      );

      if (viewingCertificate?.id === certificate.id) {
        setViewingCertificate((prev) => (
          prev ? { ...prev, status: 'CERTIFIED', rejectionReason: null } : prev
        ));
      }

      refresh();
    } catch (_error) {
      // Error toast is handled by useAsyncOperation
    }
  };

  const openInvalidateModal = (certificate) => {
    if (!certificate?.id) return;
    setCertificateToReject(certificate);
    setRejectionReason('');
    setIsRejectModalOpen(true);
  };

  const closeInvalidateModal = () => {
    if (reviewLoading) return;
    setIsRejectModalOpen(false);
    setCertificateToReject(null);
    setRejectionReason('');
  };

  const confirmInvalidate = async () => {
    if (!certificateToReject?.id) return;
    try {
      const cleanReason = String(rejectionReason || '').trim();
      await executeReview(
        () =>
          certificatesAPI.review(certificateToReject.id, {
            action: 'REJECT',
            rejectionReason: cleanReason || undefined
          }),
        {
          showSuccessToast: true,
          successMessage: t('certificates.certificateRejected'),
        }
      );

      if (viewingCertificate?.id === certificateToReject.id) {
        setViewingCertificate((prev) => (
          prev
            ? {
                ...prev,
                status: 'REJECTED',
                rejectionReason: cleanReason || null
              }
            : prev
        ));
      }

      closeInvalidateModal();
      refresh();
    } catch (_error) {
      // Error toast is handled by useAsyncOperation
    }
  };

  const allColumns = [
    // Certificate number column hidden as requested
    // {
    //   key: 'certificateNumber',
    //   title: t('certificates.certificateNumber'),
    //   sortable: true,
    //   render: (value) => (
    //     <div className="font-medium text-blue-600">{value}</div>
    //   ),
    // },
    ...(isOperator() ? [] : [{
      key: 'client',
      title: t('certificates.client'),
      render: (value, row) => {
        const certified = String(row.status || '').toUpperCase() === 'CERTIFIED';
        return (
          <div>
            <div className="flex items-start gap-2">
              <div className="font-medium text-gray-900 min-w-0 flex-1">
                {row.client?.companyName}
              </div>
              {certified && (
                <CheckCircleIcon
                  className="h-4 w-4 text-green-600 shrink-0 mt-0.5"
                  aria-hidden
                  title={t('certificates.statusCertified')}
                />
              )}
            </div>
            <div className="text-sm text-gray-500">{row.client?.contactName}</div>
          </div>
        );
      },
    }]),
    {
      key: 'product',
      title: t('certificates.product'),
      render: (value, row) => (
        <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">
          {row.product?.name}
        </span>
      ),
    },
    {
      key: 'ddpManutentionNumber',
      title: t('loads.modal.ddpNumber'),
      render: (value, row) => (
        <span className="font-mono text-sm text-gray-700">
          {row.ddpManutentionNumber ?? '—'}
        </span>
      ),
    },
    {
      key: 'date',
      title: t('certificates.certificateDate'),
      render: (value) => formatDate(value),
    },
    {
      key: 'totalLoads',
      title: t('certificates.totalLoads'),
      render: (value) => (
        <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">
          {value}
        </span>
      ),
    },
    {
      key: 'totalWeight',
      title: t('certificates.totalWeight'),
      render: (value) => (
        <span className="font-medium text-gray-900">{formatWeight(value, 'ton', { noUnit: true })}</span>
      ),
    },
    {
      key: 'actions',
      title: t('certificates.actions'),
      width: '220px',
      render: (value, row) => (
        <div className="flex flex-wrap items-center gap-1.5">
          <Button 
            size="sm" 
            variant="ghost" 
            onClick={() => handleView(row)}
            title={t('certificates.viewCertificate')}
          >
            <EyeIcon className="h-4 w-4" />
          </Button>
          <Button 
            size="sm" 
            variant="ghost" 
            onClick={() => handleDownload(row)}
            title={t('certificates.downloadPDF')}
          >
            <DocumentArrowDownIcon className="h-4 w-4" />
          </Button>
          {isManagerOrAdmin() && !superReadOnly && String(row.status || '').toUpperCase() === 'PENDING' && (
            <Button
              size="sm"
              variant="ghost"
              onClick={() => handleCertify(row)}
              title={t('certificates.certifyCertificate')}
              className="text-green-600 hover:text-green-700"
              loading={reviewLoading}
            >
              <CheckCircleIcon className="h-4 w-4" />
            </Button>
          )}
          {isManagerOrAdmin() && !superReadOnly && String(row.status || '').toUpperCase() === 'PENDING' && (
            <Button
              size="sm"
              variant="ghost"
              onClick={() => openInvalidateModal(row)}
              title={t('certificates.rejectCertificate')}
              className="text-amber-700 hover:text-amber-800"
              loading={reviewLoading}
            >
              <XCircleIcon className="h-4 w-4" />
            </Button>
          )}
          {!superReadOnly &&
            (isManagerOrAdmin() || (isPeseur() && String(row.status || '').toUpperCase() === 'REJECTED')) && (
            <Button 
              size="sm" 
              variant="ghost" 
              onClick={() => handleDelete(row)} 
              title={row.hasPayments ? t('certificates.cannotDeleteWithPayments') : t('certificates.deleteCertificate')} 
              className={row.hasPayments ? "text-gray-400 cursor-not-allowed" : "text-red-600 hover:text-red-700"}
              disabled={row.hasPayments}
            >
              <TrashIcon className="h-4 w-4" />
            </Button>
          )}
        </div>
      ),
    },
  ];

  // Filter columns based on user role
  const columns = allColumns;

  if (loading || clientsLoading) {
    return <LoadingSpinner />;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <DocumentTextIcon className="h-8 w-8 text-blue-600" />
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              {isClient() ? t('certificates.titleClient') : t('certificates.title')}
            </h1>
            <p className="text-gray-600">{t('certificates.subtitle')}</p>
          </div>
        </div>
        
        {(isManagerOrAdmin() || isPeseur()) && !superReadOnly && (
          <Button onClick={() => setIsGenerateModalOpen(true)}>
            <PlusIcon className="h-4 w-4 mr-2" />
            {t('certificates.generateCertificate')}
          </Button>
        )}
      </div>

      <div className="card">
        <div className="flex items-center mb-6">
          <div className="relative w-full max-w-2xl">
            <MagnifyingGlassIcon className="h-5 w-5 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none z-10" />
            <Input
              placeholder={t('certificates.searchPlaceholder')}
              value={filters.search}
              onChange={(e) => handleFilterChange('search', e.target.value)}
              className="w-full pl-10 pr-10"
            />
            {filters.search ? (
              <button
                type="button"
                onClick={() => handleFilterChange('search', '')}
                className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded-md text-gray-500 hover:text-gray-900 hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500"
                aria-label={t('common.clear')}
              >
                <XMarkIcon className="h-4 w-4" aria-hidden />
              </button>
            ) : null}
          </div>
        </div>

        {/* Filters Section - Always visible since everything is type-ahead */}
        <div className="bg-gray-50 p-4 rounded-lg mb-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Client Filter - Only for Admin/Manager */}
            {isManagerOrAdmin() && (
              <div>
                <Select
                  label={t('certificates.client')}
                  value={filters.clientId}
                  onChange={(e) => handleFilterChange('clientId', e.target.value)}
                  options={[
                    { value: '', label: t('certificates.allClients') },
                    ...(clients?.map(client => ({
                      value: client.id,
                      label: client.companyName
                    })) || [])
                  ]}
                  disabled={clientsLoading}
                />
              </div>
            )}
            
            {/* Start Date Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('certificates.startDate')}
              </label>
              <input
                type="date"
                value={filters.startDate}
                onChange={(e) => handleFilterChange('startDate', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            
            {/* End Date Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('certificates.endDate')}
              </label>
              <input
                type="date"
                value={filters.endDate}
                onChange={(e) => handleFilterChange('endDate', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>

          <div className="mt-4 pt-4 border-t border-gray-200">
            <label className="inline-flex items-center gap-2 cursor-pointer select-none">
              <input
                type="checkbox"
                className="checkbox-app"
                checked={showAllCertificates}
                onChange={handleShowAllCertificatesChange}
              />
              <span className="text-sm text-gray-800">{t('certificates.showAllCertificates')}</span>
            </label>
          </div>
        </div>

        <Table
          columns={columns}
          data={certificates}
          loading={loading}
          pagination={pagination}
          onPageChange={goToPage}
          emptyMessage={t('certificates.noCertificatesFound')}
        />
      </div>

      {/* Generate Certificate Modal */}
      <GenerateCertificateModal
        isOpen={isGenerateModalOpen}
        onClose={() => setIsGenerateModalOpen(false)}
        clients={clients}
        onSuccess={() => {
          setIsGenerateModalOpen(false);
          refresh();
        }}
      />

      {/* View Certificate Modal */}
      <ViewCertificateModal
        isOpen={isViewModalOpen}
        onClose={() => {
          setIsViewModalOpen(false);
          setViewingCertificate(null);
        }}
        certificate={viewingCertificate}
        onDownload={handleDownload}
        canReview={isManagerOrAdmin() && !superReadOnly}
        canDelete={!superReadOnly && (isManagerOrAdmin() || isPeseur())}
        canPeseurDeleteRejected={isPeseur()}
        onDelete={handleDeleteFromView}
        onCertify={handleCertify}
        onInvalidate={openInvalidateModal}
        reviewLoading={reviewLoading}
        onOpenLoad={handleOpenLoadFromCertificate}
        onOpenBill={handleOpenBillFromCertificate}
        onOpenClient={handleOpenClientFromCertificate}
      />

      <Modal
        isOpen={isRejectModalOpen}
        onClose={closeInvalidateModal}
        title={t('certificates.rejectCertificate')}
        footer={
          <>
            <Button variant="outline" onClick={closeInvalidateModal} disabled={reviewLoading}>
              {t('common.cancel')}
            </Button>
            <Button
              onClick={confirmInvalidate}
              loading={reviewLoading}
              className="bg-red-600 hover:bg-red-700 text-white border border-red-700"
            >
              {t('certificates.rejectCertificate')}
            </Button>
          </>
        }
      >
        <div className="space-y-3">
          <p className="text-sm text-gray-700">
            {t('certificates.rejectCertificate')}: "{certificateToReject?.certificateNumber}"
          </p>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('certificates.rejectionReason')} ({t('common.optional')})
            </label>
            <textarea
              value={rejectionReason}
              onChange={(e) => setRejectionReason(e.target.value)}
              placeholder={t('certificates.rejectionReasonPlaceholder')}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent"
            />
          </div>
        </div>
      </Modal>

      {/* Delete Confirmation Modal */}
      <ConfirmModal
        isOpen={isDeleteModalOpen}
        onClose={() => {
          setIsDeleteModalOpen(false);
          setSelectedCertificate(null);
          setCertificateBills([]);
        }}
        onConfirm={confirmDelete}
        loading={deleteLoading || loadingBills}
        title={t('certificates.deleteCertificate')}
        message={
          loadingBills ? (
            <div>{t('common.loading')}...</div>
          ) : (
            <div className="space-y-2">
              <p>{t('certificates.deleteConfirm')} "{selectedCertificate?.certificateNumber}"?</p>
              {certificateBills.length > 0 && (
                <div className="bg-yellow-50 border border-yellow-200 rounded p-3 mt-2">
                  <p className="text-yellow-800 font-medium text-sm">
                  {certificateBills.length === 1 
                    ? t('certificates.deleteWarningWithBills')
                    : t('certificates.deleteWarningWithBillsPlural', { count: certificateBills.length })
                  }
                  </p>
                  <ul className="list-disc list-inside mt-2 text-sm text-yellow-700 space-y-1">
                    {certificateBills.map(bill => (
                      <li key={bill.id}>
                        <span className="font-medium">{bill.billNumber}</span>
                        {bill._count?.payments > 0 && (
                          <span className="text-red-600 ml-2 font-semibold">
                            ({bill._count.payments} {t('payments.title').toLowerCase()})
                          </span>
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              <p className="text-sm text-gray-600 mt-2">{t('certificates.deleteCannotUndo')}</p>
            </div>
          )
        }
        confirmText={t('common.delete')}
        variant="danger"
      />
    </div>
  );
};

// View Certificate Modal Component
const ViewCertificateModal = ({
  isOpen,
  onClose,
  certificate,
  onDownload,
  canReview = false,
  canDelete = false,
  canPeseurDeleteRejected = false,
  onDelete,
  onCertify,
  onInvalidate,
  reviewLoading = false,
  onOpenLoad,
  onOpenBill,
  onOpenClient
}) => {
  const { t } = useTranslation();

  if (!certificate) return null;

  const formatFcfa = (amount) => {
    const value = Math.round(Number(amount || 0));
    return `${value.toLocaleString('fr-FR', { minimumFractionDigits: 0, maximumFractionDigits: 0 })} FCFA`;
  };

  const getDisplayUnit = (unit) => {
    const normalizedUnit = String(unit || '').toLowerCase();
    if (!normalizedUnit) return '-';

    // App-wide display standard is tonne even if historical values are stored as kg.
    if (normalizedUnit === 'kg') {
      return t('products.form.units.ton', { defaultValue: 'tonne' });
    }

    return t(`products.form.units.${normalizedUnit}`, { defaultValue: unit });
  };

  const totalMontantHt = Number(
    certificate.totalMontantHt ?? certificate.totalMontantHtExclTva ??
    certificate.loads?.reduce((sum, load) => sum + (Number(load.amountHtExclTva ?? load.amountHt - (load.amountTva || 0)) || 0), 0) ??
    0
  );
  const totalTva = Number(
    certificate.totalTva ??
    certificate.loads?.reduce((sum, load) => sum + (Number(load.amountTva) || 0), 0) ??
    0
  );
  const totalMontantTtc = Number(
    certificate.totalMontantTtc ??
    certificate.loads?.reduce((sum, load) => sum + (Number(load.amountTtc) || 0), 0) ??
    0
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={t('certificates.viewCertificate')}
      size="lg"
    >
      <div className="space-y-6">
        {/* Certificate Header */}
        <div className="bg-blue-50 p-4 rounded-lg">
          <div className="flex items-start justify-between gap-3 mb-2">
            <h3 className="text-lg font-semibold text-blue-900 break-all min-w-0">
              {certificate.certificateNumber}
            </h3>
            {String(certificate.status || '').toUpperCase() === 'CERTIFIED' && (
              <CheckCircleIcon
                className="h-7 w-7 text-green-600 shrink-0 mt-0.5"
                aria-hidden
                title={t('certificates.statusCertified')}
              />
            )}
          </div>
          <p className="text-blue-700 text-sm">
            {t('certificates.certificateDate')}: {formatDate(certificate.date)}
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Client Information */}
          <div className="space-y-4">
            <h4 className="text-md font-medium text-gray-900 border-b pb-2">
              {t('certificates.client')}
            </h4>
            <div className="space-y-2">
              <div>
                <span className="text-sm font-medium text-gray-700">
                  {t('clients.companyName')}:
                </span>
                <p className="text-sm">
                  {certificate.client?.id ? (
                    <button
                      type="button"
                      className="text-blue-600 hover:text-blue-800 hover:underline"
                      onClick={() => onOpenClient?.(certificate.client.id)}
                    >
                      {certificate.client?.companyName}
                    </button>
                  ) : (
                    <span className="text-gray-900">{certificate.client?.companyName}</span>
                  )}
                </p>
              </div>
              {(() => {
                const ddpManutention = certificate.loads?.find(l => l.ddp?.manutentionNumber)?.ddp?.manutentionNumber;
                return ddpManutention ? (
                  <div>
                    <span className="text-sm font-medium text-gray-700">
                      {t('loads.modal.ddpNumber')}:
                    </span>
                    <p className="text-sm text-gray-900 font-mono">{ddpManutention}</p>
                  </div>
                ) : null;
              })()}
              <div>
                <span className="text-sm font-medium text-gray-700">
                  {t('clients.contactName')}:
                </span>
                <p className="text-sm text-gray-900">{certificate.client?.contactName}</p>
              </div>
              <div>
                <span className="text-sm font-medium text-gray-700">
                  {t('clients.contactEmail')}:
                </span>
                <p className="text-sm text-gray-900">{certificate.client?.contactEmail}</p>
              </div>
            </div>
          </div>

          {/* Product Information */}
          <div className="space-y-4">
            <h4 className="text-md font-medium text-gray-900 border-b pb-2">
              {t('certificates.product')}
            </h4>
            <div className="space-y-2">
              <div>
                <span className="text-sm font-medium text-gray-700">
                  {t('products.name')}:
                </span>
                <p className="text-sm text-gray-900">{certificate.product?.name}</p>
              </div>
              <div>
                <span className="text-sm font-medium text-gray-700">
                  {t('products.unit')}:
                </span>
                <p className="text-sm text-gray-900">{getDisplayUnit(certificate.product?.unit)}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Certificate Summary */}
        <div className="bg-gray-50 p-4 rounded-lg">
          <h4 className="text-md font-medium text-gray-900 mb-3">
            {t('certificates.summary')}
          </h4>
          <div className="grid grid-cols-2 gap-4">
            <div className="text-center">
              <p className="text-2xl font-bold text-blue-600">{certificate.totalLoads}</p>
              <p className="text-sm text-gray-600">{t('certificates.totalLoads')}</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-green-600">{formatWeight(certificate.totalWeight, 'ton', { noUnit: true })}</p>
              <p className="text-sm text-gray-600">{t('certificates.totalWeight')}</p>
            </div>
          </div>
        </div>

        {Array.isArray(certificate.bills) && certificate.bills.length > 0 && (
          <div className="bg-white border border-gray-200 rounded-lg p-4">
            <h4 className="text-md font-medium text-gray-900 mb-3 border-b pb-2">
              {t('bills.title')}
            </h4>
            <div className="space-y-2">
              {certificate.bills.map((bill) => (
                <div
                  key={bill.id}
                  className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-gray-100 bg-gray-50 px-3 py-2"
                >
                  <div className="text-sm text-gray-900">
                    <span className="font-semibold">{t('bills.billNumber')}:</span>{' '}
                    {bill.id ? (
                      <button
                        type="button"
                        className="font-mono underline underline-offset-2 hover:text-blue-700"
                        onClick={() => onOpenBill?.(bill.id)}
                      >
                        {bill.billNumber || '-'}
                      </button>
                    ) : (
                      <span className="font-mono">{bill.billNumber || '-'}</span>
                    )}
                    {bill.status ? (
                      <span className="ml-2 text-xs text-gray-600">
                        ({t(`bills.${String(bill.status).toLowerCase()}`, { defaultValue: bill.status })})
                      </span>
                    ) : null}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Loads Cards */}
        {certificate.loads && certificate.loads.length > 0 && (
          <div>
            <h4 className="text-md font-medium text-gray-900 mb-3 border-b pb-2">
              {t('certificates.loadDetails')}
            </h4>
            <div className="space-y-4">
              {certificate.loads.map((load, index) => (
                <div
                  key={load.id}
                  className="bg-white border border-gray-200 rounded-lg shadow-sm p-4 space-y-3 hover:shadow-md transition-shadow"
                >
                  {/* Header Row */}
                  <div className="flex items-center justify-between pb-2 border-b border-gray-200">
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                        {t('loads.ticketNumber')}:
                      </span>
                      {load.id ? (
                        <button
                          type="button"
                          className="text-sm font-mono font-medium text-gray-900 underline underline-offset-2 hover:text-blue-700"
                          onClick={() => onOpenLoad?.(load.id)}
                        >
                          {load.ticket?.ticketNumber || '-'}
                        </button>
                      ) : (
                        <span className="text-sm font-mono font-medium text-gray-900">
                          {load.ticket?.ticketNumber || '-'}
                        </span>
                      )}
                    </div>
                    <span className="text-xs text-gray-500">
                      {formatDateTime(load.loadDate)}
                    </span>
                  </div>

                  {/* Main Content Grid */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {/* Truck Information */}
                    <div className="space-y-1">
                      <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                        {t('tables.truck')}
                      </div>
                      <div className="text-sm text-gray-900">
                        <div className="font-medium">{load.truck?.plateNumber || '-'}</div>
                        {load.truck?.driverName && (
                          <div className="text-xs text-gray-500">{load.truck.driverName}</div>
                        )}
                      </div>
                    </div>

                    {/* Weighbridge Information */}
                    <div className="space-y-1">
                      <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                        {t('weighing.weighbridge')}
                      </div>
                      <div className="text-sm text-gray-900">
                        <div>{load.weighbridge?.name || '-'}</div>
                        {load.weighbridge?.location && (
                          <div className="text-xs text-gray-500">{load.weighbridge.location}</div>
                        )}
                      </div>
                    </div>

                    {/* Port Information */}
                    <div className="space-y-1">
                      <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                        {t('weighing.port')}
                      </div>
                      <div className="text-sm text-gray-900">
                        <div>{load.port?.name || '-'}</div>
                        {load.port?.location && (
                          <div className="text-xs text-gray-500">{load.port.location}</div>
                        )}
                      </div>
                    </div>

                    {/* Weights Section */}
                    <div className="space-y-2">
                      <div className="grid grid-cols-3 gap-2">
                        <div>
                          <div className="text-xs text-gray-500 mb-1">{t('weighing.emptyWeight')}</div>
                          <div className="text-sm font-medium text-gray-900">
                            {formatWeight(load.emptyWeight, 'ton', { noUnit: true })}
                          </div>
                        </div>
                        <div>
                          <div className="text-xs text-gray-500 mb-1">{t('weighing.loadedWeight')}</div>
                          <div className="text-sm font-medium text-gray-900">
                            {formatWeight(load.loadedWeight, 'ton', { noUnit: true })}
                          </div>
                        </div>
                        <div>
                          <div className="text-xs text-gray-500 mb-1">{t('weighing.netWeight')}</div>
                          <div className="text-sm font-medium text-blue-600">
                            {formatWeight(load.netWeight, 'ton', { noUnit: true })}
                          </div>
                        </div>
                      </div>
                      <div className="pt-2 border-t border-gray-100">
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                          <div>
                            <div className="text-xs text-gray-500 mb-1">{t('certificates.montantHt', { defaultValue: 'Montant HT' })}</div>
                            <div className="text-sm font-semibold text-gray-900">
                              {formatFcfa(load.amountHtExclTva ?? (Number(load.amountHt) - Number(load.amountTva || 0)))}
                            </div>
                          </div>
                          <div>
                            <div className="text-xs text-gray-500 mb-1">{t('certificates.ts', { defaultValue: 'TS' })}</div>
                            <div className="text-sm font-semibold text-gray-900">
                              {load.amountTs != null && Number(load.amountTs) > 0 ? formatFcfa(load.amountTs) : '-'}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Financial Totals */}
        <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
          <h4 className="text-md font-medium text-gray-900 mb-3">
            {t('certificates.totals', { defaultValue: 'Totaux' })}
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div className="bg-white rounded-md p-3 border border-gray-200">
              <p className="text-xs text-gray-500 uppercase tracking-wide">{t('certificates.montantHt', { defaultValue: 'Montant HT' })}</p>
              <p className="text-lg font-semibold text-gray-900 mt-1">{formatFcfa(totalMontantHt)}</p>
            </div>
            <div className="bg-white rounded-md p-3 border border-gray-200">
              <p className="text-xs text-gray-500 uppercase tracking-wide">{t('certificates.tva', { defaultValue: 'TVA' })}</p>
              <p className="text-lg font-semibold text-gray-900 mt-1">{formatFcfa(totalTva)}</p>
            </div>
            <div className="bg-white rounded-md p-3 border border-gray-200">
              <p className="text-xs text-gray-500 uppercase tracking-wide">{t('certificates.montantTtc', { defaultValue: 'MONTANT TTC' })}</p>
              <p className="text-lg font-semibold text-gray-900 mt-1">{formatFcfa(totalMontantTtc)}</p>
            </div>
          </div>
        </div>

        {/* Generated Date */}
        <div className="text-sm text-gray-500 text-center">
          {t('certificates.generated')}: {formatDate(certificate.generatedDate)}
        </div>

        {/* Actions */}
        <div className="flex justify-end space-x-3 pt-4 border-t">
          <Button variant="outline" onClick={onClose}>
            {t('common.close')}
          </Button>
          {canDelete && String(certificate.status || '').toUpperCase() !== 'CERTIFIED' && (
            (canPeseurDeleteRejected
              ? String(certificate.status || '').toUpperCase() === 'REJECTED'
              : true
            ) && (
              <Button
                variant="outline"
                onClick={() => onDelete?.(certificate)}
                className="border-red-200 bg-red-50 text-red-700 hover:bg-red-100"
              >
                <TrashIcon className="h-4 w-4 mr-2" />
                {t('certificates.deleteCertificate')}
              </Button>
            )
          )}
          {canReview && String(certificate.status || '').toUpperCase() === 'PENDING' && (
            <Button
              onClick={() => onCertify?.(certificate)}
              loading={reviewLoading}
              className="bg-green-600 hover:bg-green-700 text-white"
            >
              <CheckCircleIcon className="h-4 w-4 mr-2" />
              {t('certificates.certifyCertificate')}
            </Button>
          )}
          {canReview && String(certificate.status || '').toUpperCase() === 'PENDING' && (
            <Button
              variant="outline"
              onClick={() => onInvalidate?.(certificate)}
              loading={reviewLoading}
              className="border-red-200 bg-red-50 text-red-700 hover:bg-red-100"
            >
              <XCircleIcon className="h-4 w-4 mr-2" />
              {t('certificates.rejectCertificate')}
            </Button>
          )}
          <Button onClick={() => onDownload(certificate)}>
            <DocumentArrowDownIcon className="h-4 w-4 mr-2" />
            {t('certificates.downloadPDF')}
          </Button>
        </div>
      </div>

    </Modal>
  );
};

// Generate Certificate Modal Component
const GenerateCertificateModal = ({
  isOpen,
  onClose,
  clients,
  onSuccess,
}) => {
  const { t } = useTranslation();
  const previewRequestIdRef = useRef(0);
  const [selectedClient, setSelectedClient] = useState('');
  const [selectedDate, setSelectedDate] = useState('');
  const [availableDates, setAvailableDates] = useState([]);
  const [loadingDates, setLoadingDates] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);

  const resetGenerateFormState = useCallback(() => {
    setSelectedClient('');
    setSelectedDate('');
    setAvailableDates([]);
    previewRequestIdRef.current += 1;
    setLoadingPreview(false);
    setPreviewData(null);
  }, []);

  // Form submission
  const { submit: submitGenerate, loading: generateLoading } = useFormSubmit(
    (data) => certificatesAPI.generateDaily(data),
    {
      onSuccess: (result) => {
        resetGenerateFormState();
        onSuccess();
      },
      successMessage: t('certificates.certificatesGenerated'),
    }
  );

  // Load available dates when client is selected
  React.useEffect(() => {
    if (selectedClient) {
      setLoadingDates(true);
      setPreviewData(null);
      certificatesAPI.getAvailableDates(selectedClient)
        .then(response => {
          const nextAvailableDates = response.data.availableDates || [];
          setAvailableDates(nextAvailableDates);
          setSelectedDate((prev) =>
            nextAvailableDates.some((d) => d.date === prev) ? prev : ''
          );
        })
        .catch(error => {
          console.error('Error loading available dates:', error);
          setAvailableDates([]);
          setSelectedDate('');
          setPreviewData(null);
        })
        .finally(() => {
          setLoadingDates(false);
        });
    } else {
      setAvailableDates([]);
      setSelectedDate('');
      setPreviewData(null);
    }
  }, [selectedClient]);

  // Load preview when both client and date are selected
  React.useEffect(() => {
    if (selectedClient && selectedDate) {
      const requestId = ++previewRequestIdRef.current;
      setLoadingPreview(true);
      certificatesAPI.previewDaily(selectedClient, selectedDate)
        .then(response => {
          if (previewRequestIdRef.current !== requestId) return;
          setPreviewData(response.data);
        })
        .catch(error => {
          if (previewRequestIdRef.current !== requestId) return;
          console.error('Error loading preview:', error);
          setPreviewData(null);
        })
        .finally(() => {
          if (previewRequestIdRef.current !== requestId) return;
          setLoadingPreview(false);
        });
    } else {
      previewRequestIdRef.current += 1;
      setLoadingPreview(false);
      setPreviewData(null);
    }
  }, [selectedClient, selectedDate]);

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!selectedClient || !selectedDate) {
      return;
    }
    
    submitGenerate({
      clientId: selectedClient,
      date: selectedDate
    });
  };

  const handleClose = () => {
    resetGenerateFormState();
    onClose();
  };

  const selectedDateIsAvailable = availableDates.some((dateInfo) => dateInfo.date === selectedDate);
  const hasCertifiableLoads = Boolean(
    previewData &&
    Array.isArray(previewData.groups) &&
    previewData.groups.length > 0 &&
    Number(previewData.includedLoads || 0) > 0
  );
  const disableGenerateButton =
    generateLoading ||
    loadingDates ||
    loadingPreview ||
    !selectedClient ||
    !selectedDate ||
    !selectedDateIsAvailable ||
    !hasCertifiableLoads;

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={t('certificates.generate.title')}
      size="lg"
      footer={
        <>
          <Button variant="outline" onClick={handleClose} disabled={generateLoading}>
            {t('certificates.generate.cancel')}
          </Button>
          <Button 
            type="submit" 
            loading={generateLoading} 
            form="generate-certificate-form"
            disabled={disableGenerateButton}
          >
            {t('certificates.generate.generateButton')}
          </Button>
        </>
      }
    >
      <form id="generate-certificate-form" onSubmit={handleSubmit} className="space-y-4">
        <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
          <div className="flex">
            <CalendarIcon className="h-5 w-5 text-blue-400 mr-2" />
            <div className="text-sm text-blue-700">
              <p className="font-medium">{t('certificates.generate.howItWorks')}</p>
              <p>{t('certificates.generate.howItWorksDesc')}</p>
            </div>
          </div>
        </div>

        {/* Client Selection */}
        <SearchableSelect
          label={t('certificates.generate.clientLabel')}
          value={selectedClient}
          onChange={(e) => setSelectedClient(e.target.value)}
          options={[
            { value: '', label: t('certificates.generate.clientPlaceholder') },
            ...(clients?.map((client) => ({
              value: client.id,
              label: client.companyName
            })) || [])
          ]}
          placeholder={t('certificates.generate.clientPlaceholder')}
          required
        />

        {/* Date Selection */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {t('certificates.generate.dateLabel')} *
          </label>
          {loadingDates ? (
            <div className="text-sm text-gray-500">{t('certificates.generate.loadingDates')}</div>
          ) : availableDates.length > 0 ? (
            <select
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              required
            >
              <option value="">{t('certificates.generate.datePlaceholder')}</option>
              {availableDates.map((dateInfo) => (
                <option key={dateInfo.date} value={dateInfo.date}>
                  {dateInfo.displayDate}
                </option>
              ))}
            </select>
          ) : selectedClient ? (
            <div className="text-sm text-gray-500 py-2">{t('certificates.generate.noLoadsFound')}</div>
          ) : (
            <div className="text-sm text-gray-500 py-2">{t('certificates.generate.selectClientFirst')}</div>
          )}
        </div>

        {/* Preview of loads that will be included */}
        {loadingPreview && (
          <div className="bg-gray-50 border border-gray-200 rounded-md p-3">
            <p className="text-sm text-gray-600">{t('certificates.generate.loadingPreview')}</p>
          </div>
        )}

        {previewData && !loadingPreview && (
          <div className="bg-blue-50 border border-blue-200 rounded-md p-4 space-y-4">
            <div>
              <h4 className="text-sm font-semibold text-blue-900 mb-2">
                {t('certificates.generate.loadsPreview')}
              </h4>
              <div className={`grid gap-4 text-sm ${previewData.alreadyCertificatedLoads > 0 ? 'grid-cols-2 md:grid-cols-4' : 'grid-cols-3'}`}>
                <div>
                  <span className="text-blue-700 font-medium">{t('certificates.generate.totalLoads')}:</span>
                  <span className="ml-2 text-blue-900">{previewData.totalLoads}</span>
                </div>
                <div>
                  <span className="text-green-700 font-medium">{t('certificates.generate.includedLoads')}:</span>
                  <span className="ml-2 text-green-900">{previewData.includedLoads}</span>
                </div>
                {previewData.alreadyCertificatedLoads > 0 && (
                  <div>
                    <span className="text-orange-700 font-medium">{t('certificates.generate.alreadyCertificatedLoads')}:</span>
                    <span className="ml-2 text-orange-900">{previewData.alreadyCertificatedLoads}</span>
                  </div>
                )}
                <div>
                  <span className="text-red-700 font-medium">{t('certificates.generate.excludedLoads')}:</span>
                  <span className="ml-2 text-red-900">{previewData.excludedLoads}</span>
                </div>
              </div>
            </div>

            {/* Show certificate groups that will be created */}
            {previewData.groups && previewData.groups.length > 0 && (
              <div>
                <p className="text-xs font-medium text-blue-800 mb-2">
                  {t('certificates.generate.certificatesToBeCreated')} ({previewData.groups.length}):
                </p>
                <div className="space-y-2">
                  {previewData.groups.map((group, index) => (
                    <div key={index} className="bg-white rounded p-2 border border-blue-200">
                      <div className="text-xs font-medium text-blue-900">
                        {group.product?.name} - {group.locationName}
                      </div>
                      <div className="text-xs text-blue-700 mt-1">
                        {t('certificates.generate.loadsCount')}: {group.totalLoads} | {t('certificates.totalWeight')}: {formatWeight(group.totalWeight, 'ton', { noUnit: true })}
                      </div>
                      {group.loads && group.loads.length > 0 && (
                        <div className="text-xs text-gray-600 mt-2 pl-1 border-l-2 border-blue-200 space-y-1">
                          {group.loads.map((load, i) => (
                            <div key={load.id || i}>
                              {t('loads.ticketNumber')}: {load.ticket?.ticketNumber || '-'}
                              {load.truck?.plateNumber && ` — ${load.truck.plateNumber}`}
                              {load.netWeight != null && ` — ${formatWeight(load.netWeight, 'kg', { noUnit: true })} kg`}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Show already-certificated loads */}
            {previewData.alreadyCertificatedLoads > 0 && previewData.alreadyCertificatedLoadsDetails && previewData.alreadyCertificatedLoadsDetails.length > 0 && (
              <div>
                <p className="text-xs font-medium text-orange-800 mb-2">
                  {t('certificates.generate.alreadyCertificatedLoadsTitle')} ({previewData.alreadyCertificatedLoads}):
                </p>
                <div className="max-h-40 overflow-y-auto space-y-1">
                  {previewData.alreadyCertificatedLoadsDetails.map((load, index) => (
                    <div key={index} className="bg-white rounded p-2 border border-orange-200 text-xs">
                      <div className="text-orange-900">
                        <span className="font-medium">{load.truck}</span> - {load.product}
                        {load.ticketNumber ? ` (${load.ticketNumber})` : ''}
                      </div>
                      <div className="text-orange-700 mt-1">
                        {t('certificates.generate.exclusionReason')}: {load.reason === 'Already covered by existing certificate' ? t('certificates.generate.alreadyCertificatedReason') : load.reason}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Show excluded loads with reasons */}
            {previewData.excludedLoads > 0 && previewData.excludedLoadsDetails && previewData.excludedLoadsDetails.length > 0 && (
              <div>
                <p className="text-xs font-medium text-red-800 mb-2">
                  {t('certificates.generate.excludedLoadsTitle')}:
                </p>
                <div className="max-h-40 overflow-y-auto space-y-1">
                  {previewData.excludedLoadsDetails.map((load, index) => (
                    <div key={index} className="bg-white rounded p-2 border border-red-200 text-xs">
                      <div className="text-red-900">
                        <span className="font-medium">{load.truck}</span> - {load.product}
                        {load.ticketNumber ? ` (${load.ticketNumber})` : ''}
                      </div>
                      <div className="text-red-700 mt-1">
                        {t('certificates.generate.exclusionReason')}: {load.reason}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </form>
    </Modal>
  );
};

export default CertificatesPage;